/**
 * Piece Component
 *
 * Renders a tetromino piece (T, S, O, L, or bonus) on the grid.
 * Supports:
 * - Multiple piece types with distinct colors
 * - Rotation (0, 1, 2, 3 representing 90-degree increments)
 * - Positioning on the grid
 * - Visual states (placed/unplaced)
 * - Click interactions
 * - Accessibility features
 */

import { getPieceDefinition } from '../utils/pieceDefinitions';
import { rotatePiece } from '../utils/pieceRotation';

const CELL_SIZE = 44; // pixels - matches GridCell size

export default function Piece({
  type,
  position = { x: 0, y: 0 },
  rotation = 0,
  isPlaced = false,
  onClick,
}) {
  // Validate piece type and get definition
  if (!type) {
    throw new Error('Piece component requires a type prop');
  }

  let pieceDefinition;
  try {
    pieceDefinition = getPieceDefinition(type);
  } catch (error) {
    throw new Error(`Invalid piece type: ${type}`);
  }

  const { shape, color, name } = pieceDefinition;

  // Apply rotation to shape
  let rotatedShape = shape;
  for (let i = 0; i < rotation; i++) {
    rotatedShape = rotatePiece(rotatedShape, 'cw');
  }

  // Calculate absolute positions for each square based on piece position
  const squares = rotatedShape.map((coord, index) => ({
    id: `${type}-square-${index}`,
    x: position.x + coord.x,
    y: position.y + coord.y,
    relativeX: coord.x,
    relativeY: coord.y,
  }));

  // Determine opacity based on placement state
  const opacity = isPlaced ? 'opacity-60' : 'opacity-90';

  // Special styling for bonus piece with neon glow
  const isBonusPiece = type === 'bonus';
  const borderStyle = isBonusPiece
    ? 'border-2 border-neon-yellow shadow-glow-yellow'
    : 'border border-white/50';

  /**
   * Handle piece click
   */
  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <div
      data-testid={`piece-${type}`}
      data-position-x={position.x}
      data-position-y={position.y}
      data-rotation={rotation}
      aria-label={`${name} at position ${position.x}, ${position.y}`}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      className="relative cursor-pointer select-none"
      style={{
        // Position absolutely if in a grid context
        position: 'absolute',
        left: `${position.x * CELL_SIZE}px`,
        top: `${position.y * CELL_SIZE}px`,
        pointerEvents: onClick ? 'auto' : 'none',
      }}
    >
      {squares.map((square) => (
        <div
          key={square.id}
          data-testid={`piece-square-${square.relativeX}-${square.relativeY}`}
          data-x={square.relativeX}
          data-y={square.relativeY}
          className={`absolute ${opacity} ${borderStyle} transition-all duration-200`}
          style={{
            width: `${CELL_SIZE}px`,
            height: `${CELL_SIZE}px`,
            backgroundColor: color,
            left: `${square.relativeX * CELL_SIZE}px`,
            top: `${square.relativeY * CELL_SIZE}px`,
          }}
        />
      ))}
    </div>
  );
}
